/**
 * Privacy Policy
 *
 * Required for launch:
 *   - Stripe checkout compliance review expects a public privacy URL.
 *   - K-12 teachers entering school emails will rightly look for this.
 *   - Apple/Google ad reviewers expect it linked from the marketing
 *     site footer.
 *
 * This is a working draft authored without a lawyer. Copy reflects the
 * actual data practices of TeacherDashboard.ai as of April 2026:
 *   - We collect: account email (via Stytch / Google OAuth), the
 *     teacher's name + region selected during onboarding, and the
 *     content they generate (LS comments, rubrics, etc.) saved to
 *     their account history.
 *   - We do NOT collect student personal info — teachers enter only
 *     a student first name into the comment generator, no surnames,
 *     dates of birth, or identifiers.
 *   - Generated content is stored in MongoDB Atlas (us-east).
 *   - Auth tokens via Stytch (us-central).
 *   - Payments via Stripe.
 *
 * Update: this file before announcing publicly. A lawyer should review.
 */
import { Link } from "react-router-dom";
import Logo from "../../components/ui/Logo";

const LAST_UPDATED = "April 26, 2026";

export default function PrivacyPage() {
  return (
    <div className="min-h-screen bg-white text-stone-800">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[100] focus:px-3 focus:py-2 focus:bg-brand-700 focus:text-white focus:rounded-lg focus:shadow-lg focus:outline-none focus:ring-2 focus:ring-brand-500/40 focus:ring-offset-2"
      >
        Skip to main content
      </a>
      <header className="border-b border-stone-100">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Logo className="w-8 h-8" aria-hidden="true" />
            <span className="font-bold text-stone-900">TeacherDashboard</span>
          </Link>
          <Link to="/" className="text-sm text-stone-600 hover:text-stone-900">
            Back to home
          </Link>
        </div>
      </header>
      <main id="main" tabIndex={-1} className="max-w-3xl mx-auto px-6 py-12 prose prose-stone">
        <h1>Privacy Policy</h1>
        <p className="text-sm text-stone-500">Last updated: {LAST_UPDATED}</p>

        <h2>What we collect</h2>
        <p>
          When you create a TeacherDashboard account, we collect the email
          address you sign in with through Google. During onboarding, we ask
          for the name you would like used in greetings, your country and
          region, optional school board, and the grades and subjects you
          teach. We use this information to tailor the tools shown in your
          dashboard.
        </p>
        <p>
          When you generate report card comments, rubrics, or related
          content, we save that content to your account so you can reuse and
          edit it later. The inputs you provide to the tools (a student
          first name, learning skill levels, observation notes, and similar)
          are processed by our AI provider to generate the output.
        </p>
        <p>
          We deliberately do not ask for and do not want student last names,
          full legal names, dates of birth, government identifiers, contact
          information, photos, addresses, or any health information. Our
          tools work with a first name only.
        </p>

        <h2>How we use what you give us</h2>
        <ul>
          <li>To generate the report comments, rubrics, and other outputs you ask for.</li>
          <li>To save your work to your account so you can come back to it.</li>
          <li>To learn your writing style when you opt into voice training, so the AI sounds more like you.</li>
          <li>To process your subscription payment if you choose to upgrade.</li>
          <li>To respond to questions when you email us at hello@teacherdashboard.ai.</li>
        </ul>
        <p>
          We do not sell your information. We do not show ads in the
          product. We do not use your generated content to train AI
          models for other customers.
        </p>

        <h2>Where your information lives</h2>
        <ul>
          <li>Account database: MongoDB Atlas (United States).</li>
          <li>Authentication: Stytch (United States).</li>
          <li>Hosting: Google Cloud Run (United States).</li>
          <li>AI generation: Google Gemini (United States), through their developer API. Inputs and outputs are subject to Google's API terms.</li>
          <li>Payments: Stripe. We never see or store your card details.</li>
        </ul>

        <h2>Your choices</h2>
        <p>
          You can edit or delete any saved comment, rubric, or voice
          training example at any time from inside the product. You can
          delete your account by emailing hello@teacherdashboard.ai. When you
          delete your account, we delete the content you saved.
        </p>

        <h2>Children's data</h2>
        <p>
          TeacherDashboard is sold to teachers, not directly to students or
          families. The product is intended to be used by adults for the
          purpose of writing reports. We do not knowingly collect personal
          information from children. The student first names a teacher
          enters as input are processed for the purpose of producing the
          requested output and stored only to the extent the teacher saves
          their generated comment.
        </p>

        <h2>Changes to this policy</h2>
        <p>
          If we change how we handle data, we will update this page and the
          date above. If the change is material we will notify users by
          email.
        </p>

        <h2>Contact</h2>
        <p>
          Questions about privacy: <a href="mailto:hello@teacherdashboard.ai">hello@teacherdashboard.ai</a>.
        </p>

        <p className="mt-12 text-sm">
          See also: <Link to="/terms" className="text-brand-700 underline">Terms of Service</Link>.
        </p>
      </main>
    </div>
  );
}
